import type { SigninAnnouncementSelect } from 'types';
import type { SigninReply } from 'definitions';
import type { SigninAnnouncementFormValues } from '@/state/useSigninPanel';
import { SIGNIN_HISTORY_LIMIT } from 'definitions';

export const blankAnnouncement = (): SigninAnnouncementFormValues => ({
  active: false,
  publishedAt: Date.now(),
  intro: '',
  headline: '',
  body: '',
  replies: [],
});

export const toFormValues = (row: Readonly<SigninAnnouncementSelect>): SigninAnnouncementFormValues => ({
  active: row.active,
  publishedAt: row.publishedAt,
  intro: row.intro,
  headline: row.headline,
  body: row.body,
  replies: structuredClone(row.replies),
});

const pad = (value: number): string => String(value).padStart(2, '0');

/** What an <input type="date"> wants: the local calendar day, YYYY-MM-DD. */
export const toDateInput = (timestamp: number): string => {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

type ResolveDateParams = { input: string; current: number };

/**
 * A date input only carries a day, but the stored value is a timestamp that orders
 * the history. Two things follow:
 *
 * - An untouched date keeps its exact timestamp. Re-saving must not nudge an
 *   announcement published at 16:40 to noon, which could reorder it against another
 *   one published the same day.
 * - A changed date is set to noon local time rather than midnight, so converting
 *   between the viewer's zone and UTC cannot move it to the neighbouring day.
 */
export const resolvePublishedAt = ({ input, current }: ResolveDateParams): number => {
  if (!input) return current;
  if (input === toDateInput(current)) return current;

  const parsed = new Date(`${input}T12:00:00`).getTime();
  return Number.isFinite(parsed) ? parsed : current;
};

type LiveRow = Pick<SigninAnnouncementSelect, 'id' | 'active' | 'publishedAt'>;

/**
 * Which announcements the login screen is showing right now: the newest few of the
 * published ones. The same rule as the public route, restated here so the editor can
 * label each row. A published announcement outside the window is not live, which is
 * the state an author most needs to be told about.
 */
export const liveAnnouncementIds = (rows: readonly LiveRow[]): number[] =>
  rows
    .filter((row) => row.active)
    .toSorted((a, b) => b.publishedAt - a.publishedAt || b.id - a.id)
    .slice(0, SIGNIN_HISTORY_LIMIT)
    .map((row) => row.id);

export type AnnouncementStatus = 'live' | 'history' | 'draft';

type StatusParams = { row: LiveRow; liveIds: readonly number[] };

export const announcementStatus = ({ row, liveIds }: StatusParams): AnnouncementStatus => {
  if (!row.active) return 'draft';
  return liveIds.includes(row.id) ? 'live' : 'history';
};

export const BLANK_TEXT_REPLY: SigninReply = { question: '', kind: 'text', text: '' };
export const BLANK_LINK_REPLY: SigninReply = { question: '', kind: 'link', label: '', url: '' };

type SwitchKindParams = { reply: SigninReply; kind: SigninReply['kind'] };

/** Changing a question's answer type keeps the question and drops the old answer. */
export const switchReplyKind = ({ reply, kind }: SwitchKindParams): SigninReply => {
  if (reply.kind === kind) return reply;
  return kind === 'link'
    ? { ...BLANK_LINK_REPLY, question: reply.question }
    : { ...BLANK_TEXT_REPLY, question: reply.question };
};
